import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Bell, Users, Coins } from 'lucide-react';
import { useAuraAgent } from '@/hooks/useAuraAgent';
import { useAuth } from '@/hooks/useAuth';
import { useStyleCredits } from '@/hooks/useStyleCredits';
import { ChatInterface } from './ChatInterface';
import { ObservationDashboard } from './ObservationDashboard';
import { CameraCapture } from './CameraCapture';
import { ProfileDrawer } from './ProfileDrawer';
import { OOTDRatingModal } from './OOTDRatingModal';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { NotificationCenter } from '@/components/notifications/NotificationCenter';
import { MessagingModal } from '@/components/messaging/MessagingModal';
import { SettingsModal } from '@/components/settings/SettingsModal';
import { CreditShopModal } from '@/components/modals/CreditShopModal';
import { StyleQuiz, StylePreferences } from '@/components/onboarding/StyleQuiz';
import { OnboardingModal } from '@/components/onboarding/OnboardingModal';
import { VirtualTryOn } from '@/components/tryon/VirtualTryOn';
import { ChallengesView } from '@/components/challenges/ChallengesView';
import { StreakBadge } from '@/components/gamification/StreakBadge';
import { ReferralModal } from '@/components/gamification/ReferralModal';
import { AchievementsGrid } from '@/components/gamification/AchievementsGrid';
import { StyleCredits } from '@/components/ui/StyleCredits';
import { cn } from '@/lib/utils';
import auraLogo from '@/assets/aura-logo.png';
import { toast } from 'sonner';

interface AuraAgentScreenProps {
  onShowOnboarding?: boolean;
  onOnboardingComplete?: () => void;
}

export const AuraAgentScreen: React.FC<AuraAgentScreenProps> = ({
  onShowOnboarding = false,
  onOnboardingComplete,
}) => {
  const { 
    messages, 
    isProcessing, 
    sendMessage, 
    executeAction,
    messagesEndRef,
    context,
  } = useAuraAgent();
  
  const { user, profile } = useAuth();
  const { balance } = useStyleCredits();
  
  // UI States
  const [showCamera, setShowCamera] = useState(false);
  const [showProfileDrawer, setShowProfileDrawer] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMessaging, setShowMessaging] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showStyleQuiz, setShowStyleQuiz] = useState(false);
  const [showCreditShop, setShowCreditShop] = useState(false);
  const [creditShopTab, setCreditShopTab] = useState<'shop' | 'history'>('shop');
  const [showOnboarding, setShowOnboarding] = useState(onShowOnboarding);
  const [showVirtualTryOn, setShowVirtualTryOn] = useState(false);
  const [showChallenges, setShowChallenges] = useState(false);
  const [showOOTDRating, setShowOOTDRating] = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  const [showAchievements, setShowAchievements] = useState(false);
  const [tryOnOutfit, setTryOnOutfit] = useState<any>(null);

  // Get the latest display mode from messages
  const latestDisplayMessage = [...messages].reverse().find(m => m.display);
  const currentDisplay = latestDisplayMessage?.display || 'chat';
  const currentData = latestDisplayMessage?.data;

  const handleCameraCapture = async (file: File, analysis?: any) => {
    setShowCamera(false);
    
    executeAction({
      type: 'add_item',
      label: 'Add item',
      params: {
        file,
        metadata: analysis ? {
          name: analysis.suggestedName,
          type: analysis.type,
          color: analysis.color,
          occasion: analysis.occasion,
          mood: analysis.mood,
          price: analysis.estimatedPrice,
          brand: analysis.brand,
        } : undefined,
      },
    });
  };

  const handleAction = (action: any) => {
    switch (action.type) {
      case 'open_camera':
        setShowCamera(true);
        break;
      case 'open_profile':
        setShowProfileDrawer(true);
        break;
      case 'open_settings':
        setShowSettings(true);
        break;
      case 'open_style_quiz':
        setShowStyleQuiz(true);
        break;
      case 'upgrade':
      case 'buy_credits':
        setCreditShopTab('shop');
        setShowCreditShop(true);
        break;
      case 'credit_history':
        setCreditShopTab('history');
        setShowCreditShop(true);
        break;
      case 'virtual_tryon':
        setTryOnOutfit(action.params?.outfit);
        setShowVirtualTryOn(true);
        break;
      case 'open_challenges':
        setShowChallenges(true);
        break;
      case 'rate_ootd':
        setShowOOTDRating(true);
        break;
      case 'open_referral':
        setShowReferral(true);
        break;
      case 'open_achievements':
        setShowAchievements(true);
        break;
      default:
        executeAction(action);
    }
  };

  // Removed unused upgrade handler since we now use credits

  const handleStyleQuizComplete = (preferences: StylePreferences) => {
    setShowStyleQuiz(false);
    toast.success('Your style profile is updated!');
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    onOnboardingComplete?.();
    // Show style quiz after onboarding if not completed
    if (!profile?.quiz_completed) {
      setTimeout(() => setShowStyleQuiz(true), 500);
    }
  };

  const auraPoints = profile?.aura_points ?? 0;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Unified Minimal Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-xl border-b border-border/50 safe-top">
        <div className="flex items-center justify-between px-4 h-14 max-w-3xl mx-auto">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <img src={auraLogo} alt="AURA" className="w-7 h-7" />
          </div>

          {/* Center - Style Credits & Status */}
          <div className="flex items-center gap-3">
            {/* Style Credits Button */}
            {user && (
              <StyleCredits 
                size="sm" 
                onClick={() => setShowCreditShop(true)}
                showWarning
              />
            )}
            
            {/* Aura Points (gamification) */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-card/50 border border-border/50"
            >
              <Sparkles size={11} className="text-aura-gold" />
              <span className="text-[10px] font-medium tabular-nums text-muted-foreground">
                {auraPoints.toLocaleString()}
              </span>
            </motion.div>
            
            {/* Streak Badge */}
            {user && <StreakBadge size="sm" />}
            
            {/* Status indicator */}
            <div className="flex items-center gap-1">
              <div className={cn(
                "w-1.5 h-1.5 rounded-full",
                isProcessing ? "bg-aura-gold animate-pulse" : "bg-green-500"
              )} />
            </div>
          </div>

          {/* Right - Referral, Notifications & Profile */}
          <div className="flex items-center gap-1">
            {user && (
              <button
                onClick={() => setShowReferral(true)}
                className="p-1.5 rounded-xl hover:bg-muted transition-colors"
                title="Invite Friends"
              >
                <Users size={18} className="text-muted-foreground" />
              </button>
            )}
            
            {user && (
              <NotificationBell onClick={() => setShowNotifications(true)} />
            )}
            
            <button
              onClick={() => setShowProfileDrawer(true)}
              className="p-1.5 rounded-xl hover:bg-muted transition-colors"
            >
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt="Profile"
                  className="w-7 h-7 rounded-lg object-cover"
                />
              ) : (
                <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center">
                  <span className="text-xs font-medium text-muted-foreground">
                    {profile?.display_name?.[0]?.toUpperCase() || 'A'}
                  </span>
                </div>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col pt-14">
        {/* Observation Dashboard */}
        <ObservationDashboard
          display={currentDisplay}
          data={currentData}
          weather={context.weather}
          wardrobeCount={context.wardrobe.length}
        />

        {/* Chat Interface */}
        <div className="flex-1 min-h-0">
          <ChatInterface
            messages={messages}
            isProcessing={isProcessing}
            onSendMessage={sendMessage}
            onAction={handleAction}
            onCameraOpen={() => setShowCamera(true)}
            messagesEndRef={messagesEndRef}
            onInsufficientCredits={() => setShowCreditShop(true)}
          />
        </div>
      </main>

      {/* Profile Drawer */}
      <ProfileDrawer
        isOpen={showProfileDrawer}
        onClose={() => setShowProfileDrawer(false)}
        onOpenMessages={() => setShowMessaging(true)}
        onOpenSettings={() => setShowSettings(true)}
        onOpenStyleQuiz={() => setShowStyleQuiz(true)}
        onOpenChallenges={() => setShowChallenges(true)}
        onOpenAchievements={() => setShowAchievements(true)}
        onOpenReferral={() => setShowReferral(true)}
        onUpgrade={() => setShowCreditShop(true)}
      />

      {/* Camera Capture Modal */}
      <CameraCapture
        isOpen={showCamera}
        onClose={() => setShowCamera(false)}
        onCapture={handleCameraCapture}
      />

      {/* Notification Center */}
      <NotificationCenter 
        isOpen={showNotifications} 
        onClose={() => setShowNotifications(false)} 
      />

      {/* Messaging Modal */}
      <MessagingModal
        isOpen={showMessaging}
        onClose={() => setShowMessaging(false)}
      />

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        isNightMode={false}
        onToggleNightMode={() => {}}
      />

      {/* Style Quiz */}
      <StyleQuiz
        isOpen={showStyleQuiz}
        onComplete={handleStyleQuizComplete}
        onClose={() => setShowStyleQuiz(false)}
      />

      {/* Onboarding */}
      <OnboardingModal
        isOpen={showOnboarding}
        onComplete={handleOnboardingComplete}
        onClose={() => setShowOnboarding(false)}
      />

      {/* Credit Shop */}
      {showCreditShop && (
        <CreditShopModal
          onClose={() => setShowCreditShop(false)}
          initialTab={creditShopTab}
        />
      )}

      {/* Virtual Try-On */}
      <VirtualTryOn
        isOpen={showVirtualTryOn}
        onClose={() => setShowVirtualTryOn(false)}
        outfit={tryOnOutfit}
      />

      {/* OOTD Rating Modal */}
      <OOTDRatingModal
        isOpen={showOOTDRating}
        onClose={() => setShowOOTDRating(false)}
        onInsufficientCredits={() => {
          setShowOOTDRating(false);
          setShowCreditShop(true);
        }}
      />

      {/* Referral Modal */}
      <ReferralModal
        isOpen={showReferral}
        onClose={() => setShowReferral(false)}
      />

      {/* Challenges Full Screen */}
      {showChallenges && (
        <div className="fixed inset-0 z-[100] bg-background overflow-y-auto">
          <div className="p-4 pt-14">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-display">Challenges</h2>
              <button
                onClick={() => setShowChallenges(false)}
                className="px-4 py-2 text-sm rounded-xl bg-secondary hover:bg-secondary/80 transition-colors"
              >
                Close
              </button>
            </div>
            <ChallengesView />
          </div>
        </div>
      )}

      {/* Achievements Full Screen */}
      {showAchievements && (
        <div className="fixed inset-0 z-[100] bg-background overflow-y-auto">
          <div className="p-4 pt-14 max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-display">Achievements</h2>
              <button
                onClick={() => setShowAchievements(false)}
                className="px-4 py-2 text-sm rounded-xl bg-secondary hover:bg-secondary/80 transition-colors"
              >
                Close
              </button>
            </div>
            <AchievementsGrid />
          </div>
        </div>
      )}

      {/* AURA Level Badge - Floating */}
      {profile?.aura_level && !showProfileDrawer && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-4 left-4 z-40"
        >
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-card/90 backdrop-blur border border-border shadow-lg">
            <Sparkles size={12} className="text-aura-gold" />
            <span className="text-[10px] font-medium uppercase tracking-wider">
              {profile.aura_level}
            </span>
          </div>
        </motion.div>
      )}
    </div>
  );
};
